import { useRef } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import type { HistoryState } from '@tanstack/history';
import type { CallSheetMembershipDto } from '@signalvote/shared';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useProspectDetail } from '@/hooks/queries';
import { ProspectProfile } from './ProspectProfile';
import { ProspectSteps, usePrefetchSteps, useStepKeys, useSteps } from './ProspectSteps';

declare module '@tanstack/history' {
  interface HistoryState {
    /** This entry is a person opened from the list under it, so closing them can go Back over it. */
    personOpenedFromList?: boolean;
  }
}

/**
 * The history state an open from a list writes (`navigate({ …, state: openedFromList })`): the entry is marked
 * as ours, so closing goes Back to the list instead of stacking a second copy of it. A shared link or a reload
 * has no mark, and closing it takes the person out of the URL instead of leaving the app.
 */
export const openedFromList = (prev: HistoryState): HistoryState => ({ ...prev, personOpenedFromList: true });

/** The history state a step writes as it replaces the entry: the open's mark stays. */
export const keptHistoryState = (prev: HistoryState): HistoryState => prev;

/**
 * A person's whole profile in a dialog over the list they were opened from — the Prospects book or a call
 * book — which stays mounted behind it, where it was. J / K and the step buttons move through `order` (the
 * list's own order) through `onStep`; `total` is the list's size for "2 of 412". Closing — X, Esc, the
 * backdrop, or removing them — goes Back when the list opened them, else asks `onDismiss` to take them out of
 * the URL.
 */
export function ProspectProfileModal({
  prospectId,
  order,
  total,
  sheets,
  onStep,
  onDismiss,
}: {
  prospectId: string;
  order: readonly string[];
  total: number;
  sheets: CallSheetMembershipDto[];
  onStep: (prospectId: string) => void;
  onDismiss: () => void;
}) {
  const router = useRouter();
  const fromList = useRouterState({ select: (state) => !!state.location.state.personOpenedFromList });
  const close = () => (fromList ? router.history.back() : onDismiss());
  const content = useRef<HTMLDivElement | null>(null);
  const steps = useSteps(order, prospectId);
  useStepKeys({ previous: steps.previous, next: steps.next, onStep, within: content });
  usePrefetchSteps(steps);
  // The profile's own read (shared, not repeated): until it lands the dialog still needs a name.
  const { data: detail } = useProspectDetail(prospectId);

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        ref={content}
        aria-describedby={undefined}
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-none flex-col gap-0 overflow-hidden p-0 sm:h-auto sm:max-h-[90vh] sm:w-[90vw] sm:max-w-[960px] sm:rounded-xl"
      >
        <div className="flex min-h-14 shrink-0 items-center justify-end border-b bg-card py-2 pl-4 pr-14 sm:pl-8">
          <ProspectSteps steps={steps} total={total} onStep={onStep} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          {!detail && <DialogTitle className="sr-only">Prospect</DialogTitle>}
          <ProspectProfile
            prospectId={prospectId}
            sheets={sheets}
            onRemoved={close}
            renderName={(name, className) => (
              <DialogTitle asChild>
                <h2 className={className}>{name}</h2>
              </DialogTitle>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
